// The Forecasting tab's Support view (2026-10-28, Patrick's ask): actuals only for now.
//   1. Volume    one weekly line, CXHub Support email plus the Bright Pattern CSS and TRQ
//                queues, with the split shown; Support's phone calls (TRQ Inbound Voice, and the
//                internal CS Support calls) beside it, not in the email total.
//   2. Staffing  handle time, the FTE the work asks for on his 23% method (CXHub email, Bright
//                Pattern email and phone apart), the CXHub time in the Support schedule and the gap.
//   3. Service   Support Email's average time to first answer against its target.
// All the arithmetic is on the server (server/src/services/forecasting-support.js); this
// only lays it out. Hours sit behind a toggle, as on the other views.
import { useEffect, useState } from 'react'
import {
  Alert, Box, CircularProgress, FormControlLabel, Paper, Switch, Table, TableBody, TableHead, TableRow,
} from '@mui/material'
import { fetchForecastSupport } from '../api.js'
import SectionHeading from './SectionHeading.jsx'
import { Cell, Head, WeekCell, Fte, Divider, rowSx } from './ForecastTableParts.jsx'
import { formatCount, formatGap, formatSeconds, gapTone, EM_DASH } from '../forecastPhoneChat.js'
import { formatAvgHours, formatShare, slaHeader, slaTone } from '../forecastEmail.js'

const TABLE_BOX = { width: 'fit-content', maxWidth: '100%' }

// Scheduled Bright Pattern queue hours of the Support teams: whole hours, a dash with no schedule.
const formatQueueHours = (h) => (typeof h === 'number' && Number.isFinite(h) ? `${Math.round(h)} h` : EM_DASH)

function VolumeTable({ report }) {
  return (
    <Paper variant="outlined" sx={{ overflowX: 'auto', ...TABLE_BOX }}>
      <Table size="small" sx={{ width: 'auto', borderCollapse: 'collapse' }}>
        <TableHead>
          <TableRow>
            <Head align="left">Week ending</Head>
            <Head left>CXHub Support email</Head>
            <Head>Bright Pattern (CSS, TRQ)</Head>
            <Head left>Total</Head>
            <Head>Bright Pattern share</Head>
            <Head left>Emails handled (CXHub + Bright Pattern)</Head>
            <Head left>TRQ Inbound Voice calls</Head>
            <Head>CS Support calls (internal)</Head>
          </TableRow>
        </TableHead>
        <TableBody>
          <Divider colSpan={8}>Recent weeks · oldest first, then this week so far</Divider>
          {report.weeks.map((row) => {
            const v = row.volume
            const partial = row.isPartial
            return (
              <TableRow key={row.week} sx={rowSx(row)}>
                <WeekCell row={row} />
                <Cell left muted={v.cxhub == null || partial} italic={partial}>{formatCount(v.cxhub)}</Cell>
                <Cell muted={v.bp == null || partial} italic={partial}>{formatCount(v.bp)}</Cell>
                <Cell left bold muted={partial} italic={partial}>{formatCount(v.total)}{partial && v.total != null ? ' so far' : ''}</Cell>
                <Cell muted>{formatShare(v.bpShare)}</Cell>
                <Cell left muted={v.handledByPeople == null || partial} italic={partial}>{formatCount(v.handledByPeople)}</Cell>
                <Cell left muted={v.phone?.trqInbound == null || partial} italic={partial}>{formatCount(v.phone?.trqInbound)}</Cell>
                <Cell muted={v.phone?.internal == null || partial} italic={partial}>{formatCount(v.phone?.internal)}</Cell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </Paper>
  )
}

function StaffingTable({ report, showHours }) {
  const a = report.assumptions
  return (
    <Paper variant="outlined" sx={{ overflowX: 'auto', ...TABLE_BOX }}>
      <Table size="small" sx={{ width: 'auto', borderCollapse: 'collapse' }}>
        <TableHead>
          <TableRow>
            <Head rowSpan={2} align="left">Week ending</Head>
            <Head colSpan={5} left>Handle time</Head>
            <Head colSpan={4} left>FTE needed (× {1 + a.shrinkageRate})</Head>
            <Head rowSpan={2} left>FTE scheduled, CXHub (÷ {a.scheduledHoursPerFte})</Head>
            <Head rowSpan={2} left>Gap, CXHub</Head>
            <Head rowSpan={2} left>Bright Pattern queue time scheduled</Head>
          </TableRow>
          <TableRow>
            <Head left>CXHub</Head>
            <Head>Bright Pattern ÷ {a.bpInFocusDivisor}</Head>
            <Head>Email, all</Head>
            <Head>TRQ Inbound Voice</Head>
            <Head>CS Support</Head>
            <Head left>CXHub email</Head>
            <Head>Bright Pattern email</Head>
            <Head>Phone</Head>
            <Head>Total</Head>
          </TableRow>
        </TableHead>
        <TableBody>
          <Divider colSpan={13}>Recent weeks · oldest first, then this week so far</Divider>
          {report.weeks.map((row) => {
            const p = row.plan
            const partial = row.isPartial
            return (
              <TableRow key={row.week} sx={rowSx(row)}>
                <WeekCell row={row} />
                <Cell left muted={row.aht.cxhubSec == null}>{formatSeconds(row.aht.cxhubSec)}</Cell>
                <Cell muted={row.aht.bpSec == null}>{formatSeconds(row.aht.bpSec)}</Cell>
                <Cell muted={row.aht.combinedSec == null}>{formatSeconds(row.aht.combinedSec)}</Cell>
                <Cell muted={row.aht.trqInboundSec == null}>{formatSeconds(row.aht.trqInboundSec)}</Cell>
                <Cell muted={row.aht.internalSec == null}>{formatSeconds(row.aht.internalSec)}</Cell>
                <Cell left muted={!p.needCxhub || partial} italic={partial}><Fte value={p.needCxhub} showHours={showHours} /></Cell>
                <Cell muted={!p.needBp || partial} italic={partial}><Fte value={p.needBp} showHours={showHours} /></Cell>
                <Cell muted={!p.needPhone || partial} italic={partial}><Fte value={p.needPhone} showHours={showHours} /></Cell>
                <Cell bold muted={!p.need || partial} italic={partial}><Fte value={p.need} showHours={showHours} /></Cell>
                <Cell left bold muted={!p.scheduled}><Fte value={p.scheduled} showHours={showHours} /></Cell>
                <Cell left bold tone={gapTone(p.gap)} muted={p.gap == null}>{formatGap(p.gap)}</Cell>
                <Cell left muted>{formatQueueHours(p.bpQueueHours)}</Cell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </Paper>
  )
}

function ServiceTable({ report }) {
  const rows = report.slaRows
  return (
    <Paper variant="outlined" sx={{ overflowX: 'auto', ...TABLE_BOX }}>
      <Table size="small" sx={{ width: 'auto', borderCollapse: 'collapse' }}>
        <TableHead>
          <TableRow>
            <Head align="left">Week ending</Head>
            {rows.map((r, i) => <Head key={r.key} left={i === 0}>{slaHeader(r)}</Head>)}
          </TableRow>
        </TableHead>
        <TableBody>
          <Divider colSpan={rows.length + 1}>Average time to first answer · recent weeks, oldest first</Divider>
          {report.weeks.map((week) => (
            <TableRow key={week.week} sx={rowSx(week)}>
              <WeekCell row={week} />
              {rows.map((r, i) => {
                const s = week.service[r.key]
                return (
                  <Cell key={r.key} left={i === 0} tone={slaTone(s?.avgHours, s?.targetHours)} muted={s?.avgHours == null} italic={week.isPartial}>
                    {s?.avgHours == null ? EM_DASH : formatAvgHours(s.avgHours)}
                  </Cell>
                )
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  )
}

// The loaded view, apart from the fetch so it can be rendered from a report alone.
export function ForecastSupportView({ report }) {
  const [showHours, setShowHours] = useState(false)
  const a = report.assumptions
  return (
    <Box>
      <SectionHeading variant="subtitle1" sx={{ mb: 1, ...TABLE_BOX }} title="Forecasting: Support" descriptionMaxWidth={1100}>
        What happened, week by week, oldest first. Actuals only so far, no forecast. One volume line for the Support team's email: CXHub
        Support email (1A Support, 1A and AM Customer Support, and the Sales Hold callback contacts; spam out) plus the Bright Pattern CSS and
        TRQ queues. Some Bright Pattern Support queues have moved to CXHub (CSS Internal and the two Customer Support mailboxes, in October), so
        a little of the same work still shows on the Bright Pattern side; they are different messages, not counted twice.
      </SectionHeading>
      <FormControlLabel
        control={<Switch size="small" checked={showHours} onChange={(e) => setShowHours(e.target.checked)} />}
        label="Show hours under each FTE"
        sx={{ mb: 1 }}
      />
      {report.warnings.includes('bp_support_missing') && (
        <Alert severity="warning" sx={{ mb: 1, maxWidth: 1100 }}>
          Schedule Hub returned no Bright Pattern Support email, so the line is CXHub alone.
        </Alert>
      )}
      {report.warnings.includes('support_phone_unavailable') && (
        <Alert severity="warning" sx={{ mb: 1, maxWidth: 1100 }}>
          Schedule Hub could not be read for Support's phone queues just now, so the need below is the email alone. Reload to try again.
        </Alert>
      )}
      {report.warnings.includes('support_phone_missing') && (
        <Alert severity="warning" sx={{ mb: 1, maxWidth: 1100 }}>
          Schedule Hub returned no Support phone calls (TRQ Inbound Voice, CS Support), so the need below is the email alone.
        </Alert>
      )}
      {report.warnings.includes('cxhub_support_missing') && (
        <Alert severity="warning" sx={{ mb: 1, maxWidth: 1100 }}>
          No CXHub Support email was found for these weeks, so the line is Bright Pattern alone.
        </Alert>
      )}

      <Box sx={{ mt: 2, ...TABLE_BOX }}>
        <SectionHeading variant="subtitle2" sx={{ mb: 0.75 }} title="1. Volume" descriptionMaxWidth={1100}>
          CXHub counts the contacts that arrived in the week, spam out (the Weekly Report's valid count). Bright Pattern is the answered
          contacts on every CSS and TRQ email queue, not the outbound ones. Emails handled is the part of the total that took agent time
          (CXHub contacts a person handled, plus the Bright Pattern answered): the count the email FTE is worked from. The two phone columns are
          calls answered, kept apart from the email line: TRQ Inbound Voice is customers calling Support, and CS Support (with CS Tech Support) is
          agents calling Support for help on an order or a fitment question. The internal calls are Support's workload, not customer demand, so
          they are not in the Phone view's volume. This week is only so far.
        </SectionHeading>
        <VolumeTable report={report} />
      </Box>

      <Box sx={{ mt: 3, ...TABLE_BOX }}>
        <SectionHeading variant="subtitle2" sx={{ mb: 0.75 }} title="2. Staffing" descriptionMaxWidth={1100}>
          The work is the contacts a person handled times handle time. CXHub's handle time is the Reporting one for Support Email (claim to
          action, pauses out, spam out). Bright Pattern's is Schedule Hub's raw handle time divided by {a.bpInFocusDivisor}, because it counts time the
          agent was not looking at the email (Power BI's in-focus time for these queues ran about 1 over {a.bpInFocusDivisor} of it in October); that is an
          estimate. FTE needed is that work ÷ {a.onQueueHoursPerFte} hours × {1 + a.shrinkageRate}, your {Math.round(a.shrinkageRate * 100)}% standard with no flat spare.
          FTE scheduled is the CXHub time in the schedule: for {a.scheduleTeams.join(' and ')}, scheduled hours × the share that is CXHub blocks,
          ÷ {a.scheduledHoursPerFte}. The gap compares that with the CXHub need only, like for like. The Bright Pattern work is answered in
          the same two teams' Bright Pattern queue time, which also carries some phone and chat, so no gap is drawn for it: its need is
          beside it, and the last column is the queue hours those teams are scheduled for, for reference. A week with no CXHub blocks
          scheduled (under 4 hours, or past the roster) has no scheduled FTE or gap. Support's need also counts its phone work, the TRQ Inbound
          Voice and CS Support calls, at Schedule Hub's own handle time (phone has no out-of-focus time, so no divisor), on the same
          {a.onQueueHoursPerFte} hours × {1 + a.shrinkageRate}. The Phone column is in the total need but not in the gap, which stays CXHub like for like.
        </SectionHeading>
        <StaffingTable report={report} showHours={showHours} />
      </Box>

      <Box sx={{ mt: 3, ...TABLE_BOX }}>
        <SectionHeading variant="subtitle2" sx={{ mb: 0.75 }} title="3. Service" descriptionMaxWidth={1100}>
          Average time from a contact arriving to its first answer, in hours, from the Reporting ASA, against each target: Support Email 12 hours,
          and Walmart 24. Walmart is a subset of Support Email: its contacts are in Support Email's number and in the volume, handle time and
          staffing above, and its own ASA is shown beside it. Bright Pattern has no usable email service level (it always reads 100%), so its queues have none.
        </SectionHeading>
        <ServiceTable report={report} />
      </Box>

      <SectionHeading variant="subtitle2" appearance="quiet" sx={{ mt: 2 }} title="Colors and notes" descriptionMaxWidth={1100}>
        Colors: gap red below 0, amber under +1; speed of answer red over the target, amber within 20% of it. "—" means no data yet, never
        zero. Italic is this week so far.
      </SectionHeading>
    </Box>
  )
}

export default function ForecastSupport() {
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let live = true
    fetchForecastSupport()
      .then((r) => { if (live) setReport(r) })
      .catch((e) => { if (live) setError(e.message) })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [])

  if (loading) return <Box sx={{ py: 2 }}><CircularProgress size={24} /></Box>
  if (error) return <Alert severity="error">{error}</Alert>
  if (!report) return null
  return <ForecastSupportView report={report} />
}
